import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ClaimSummary } from "./ClaimSummary";
import { formatClaimValue } from "@/utils/claims";
import type { OidcRuntimeConfig } from "@/auth/config";
import type { ClaimProfile } from "@/types/auth";

type SignedInViewProps = {
  config: OidcRuntimeConfig;
};

export function SignedInView({ config }: SignedInViewProps) {
  const auth = useAuth();
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const profile: ClaimProfile = auth.user?.profile ?? {};
  const profileJson = useMemo(() => JSON.stringify(profile, null, 2), [profile]);

  async function logout() {
    setLogoutError(null);

    try {
      await auth.signoutRedirect({
        id_token_hint: auth.user?.id_token,
        post_logout_redirect_uri: config.postLogoutRedirectUri
      });
    } catch (error) {
      await auth.removeUser();
      setLogoutError(
        error instanceof Error
          ? `Provider logout was unavailable, so the local session was cleared. ${error.message}`
          : "Provider logout was unavailable, so the local session was cleared."
      );
    }
  }

  return (
    <main className="app-shell">
      <section className="panel">
        <header className="top-bar">
          <div>
            <p className="eyebrow success">Authenticated</p>
            <h1>
              {formatClaimValue(
                profile["name"] ?? profile["email"] ?? profile["sub"]
              )}
            </h1>
          </div>
          <button className="button secondary" onClick={() => void logout()}>
            Log out
          </button>
        </header>

        {logoutError ? <p className="notice">{logoutError}</p> : null}

        <ClaimSummary profile={profile} />

        <section className="claims-section">
          <div>
            <h2>Profile claims</h2>
          </div>
          <pre className="code-block claims-json">{profileJson}</pre>
        </section>
      </section>
    </main>
  );
}
